import jsPDF from "jspdf";
// Registers the "NotoSansDevanagari" font with jsPDF (used for Hindi lines).
import "../fonts/NotoSansDevanagari";

// Strip the LLM's "Reference No: [Auto-numbered by system]" placeholder line.
// Anchored to its own line so it doesn't swallow the surrounding newlines.
const REFERENCE_PLACEHOLDER = /^[ \t]*[-*]?[ \t]*\**Reference No:\**[ \t]*\[Auto-numbered by system\][ \t]*\r?\n?/gim;
const DEVANAGARI = /[ऀ-ॿ]/;

/** Full BTR text for a grievance row: prefer the full content over the (truncated) summary. */
export const getBtrText = (row, override) =>
  (override || row?.btr_content || row?.btr_summary || "").replace(REFERENCE_PLACEHOLDER, "");

const sanitize = (text, keepDevanagari) => {
  let t = (text || "")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, "-")
    .replace(/[•●]/g, "-")
    .replace(/[₹]/g, "Rs.");
  // Helvetica only covers Latin-1; drop emojis/other scripts that would render as garbage.
  /* eslint-disable no-control-regex */
  t = keepDevanagari
    ? t.replace(/[^\x00-\xFFऀ-ॿ]/g, "")
    : t.replace(/[^\x00-\xFF]/g, "");
  /* eslint-enable no-control-regex */
  return t;
};

const stripInline = (text) =>
  text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/(^|\s)\*([^*\n]+)\*/g, "$1$2")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");

/**
 * Generate and download the BTR as a PDF document.
 * @param {object} row       grievance record (grievance_id, btr_* fields, category, etc.)
 * @param {string} override  optional BTR text to use instead of the stored content
 */
export function downloadBtrPdf(row, override) {
  const text = getBtrText(row, override) || "Brief Transmission Report";
  const gid = row?.grievance_id || "report";

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const left = 40;
  const width = pageW - left * 2;
  const bottom = pageH - 50;
  let y;

  const setBody = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
  };

  const drawHeader = () => {
    doc.setFillColor(49, 46, 129);
    doc.rect(0, 0, pageW, 60, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("NCW Complaint Redressal System", left, 28);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Brief Transmission Report (BTR)", left, 45);
    y = 85;
    setBody();
  };

  const ensureSpace = (needed = 14) => {
    if (y + needed > bottom) {
      doc.addPage();
      y = 50;
      setBody();
    }
  };

  const writeWrapped = (line, { bold = false, size = 10, indent = 0, color } = {}) => {
    const hasDev = DEVANAGARI.test(line);
    const clean = sanitize(line, hasDev);
    if (!clean.trim()) return;
    if (hasDev) {
      doc.setFont("NotoSansDevanagari", "normal");
    } else {
      doc.setFont("helvetica", bold ? "bold" : "normal");
    }
    doc.setFontSize(size);
    if (color) doc.setTextColor(...color);
    const lineH = size + 4;
    doc.splitTextToSize(clean, width - indent).forEach((wl) => {
      ensureSpace(lineH);
      doc.text(wl, left + indent, y);
      y += lineH;
    });
    setBody();
  };

  drawHeader();

  // Metadata block
  const meta = [
    ["Complaint ID", gid],
    ["BTR Reference", row?.btr_reference_no || "-"],
    ["Department", row?.llm_category || "-"],
    ["Category", row?.llm_subcategory || "-"],
    ["Severity", row?.severity || "-"],
    ["BTR Status", row?.btr_status || "-"],
    ["Submitted On", row?.btr_timestamp ? new Date(row.btr_timestamp).toLocaleString("en-IN") : "-"],
    ["Generated On", new Date().toLocaleString("en-IN")],
  ];
  const metaH = Math.ceil(meta.length / 2) * 16 + 14;
  doc.setFillColor(238, 242, 255);
  doc.setDrawColor(199, 210, 254);
  doc.rect(left, y - 12, width, metaH, "FD");
  doc.setFontSize(9);
  meta.forEach(([k, v], i) => {
    const col = i % 2;
    const rowIdx = Math.floor(i / 2);
    const x = left + 10 + col * (width / 2);
    const yy = y + 2 + rowIdx * 16;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(55, 48, 163);
    doc.text(`${k}:`, x, yy);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(40, 40, 40);
    const val = sanitize(String(v), false);
    doc.text(doc.splitTextToSize(val, width / 2 - 95)[0] || "", x + 80, yy);
  });
  y += metaH + 10;
  setBody();

  // Body: lightweight markdown rendering
  text.split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      y += 6;
      return;
    }
    if (/^[-*_]{3,}$/.test(line)) {
      ensureSpace(10);
      doc.setDrawColor(200, 200, 200);
      doc.line(left, y - 4, left + width, y - 4);
      y += 6;
      return;
    }
    if (/^\|?\s*:?-{2,}/.test(line) && /^[|:\-\s]+$/.test(line)) return; // table separator row
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      y += 6;
      const size = heading[1].length <= 2 ? 13 : 11;
      writeWrapped(stripInline(heading[2]), { bold: true, size, color: [49, 46, 129] });
      y += 2;
      return;
    }
    if (line.startsWith("|")) {
      const cells = line.replace(/^\||\|$/g, "").split("|").map((c) => stripInline(c.trim()));
      writeWrapped(cells.join("   |   "), { indent: 6 });
      return;
    }
    const bullet = line.match(/^([-*+]|\d+\.)\s+(.*)$/);
    if (bullet) {
      const marker = /\d+\./.test(bullet[1]) ? bullet[1] : "-";
      writeWrapped(`${marker} ${stripInline(bullet[2])}`, { indent: 12 });
      return;
    }
    const isBoldLine = /^\*\*[^*]+\*\*:?$/.test(line);
    writeWrapped(stripInline(line), { bold: isBoldLine });
  });

  // Footer with page numbers
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`BTR - Complaint ${gid}  |  NCW Complaint Redressal System`, left, pageH - 25);
    doc.text(`Page ${i} of ${pages}`, pageW - left, pageH - 25, { align: "right" });
  }

  doc.save(`BTR_${gid}.pdf`);
}
